import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AppConfig } from "../AppConfig";
import { LocalStorageManager } from "../core/storage/LocalStorageManager";
import { dataSelectors } from "../ducks/data";
import {
  formatDateTime,
  getDateTimeByHourOffset,
  isInDateRange,
  isNowInDateRange,
  isSameDate,
} from "../utils/date";

export const useMaintenance = () => {
  const maintenance = useSelector(dataSelectors.maintenance);

  const [closedPopup, setClosedPopup] = useState<boolean>(false);

  const popupTitle = useMemo(() => {
    if (!maintenance) return "";

    const isSameDateMaintenance = isSameDate(
      maintenance.start_date,
      maintenance.end_date,
    );

    const startDate = formatDateTime(
      maintenance.start_date,
      true,
      "YYYY年M月D日 {date} HH:mm",
    );
    const endDate = formatDateTime(
      maintenance.end_date,
      true,
      isSameDateMaintenance ? "HH:mm" : "YYYY年M月D日 {date} HH:mm",
    );

    return `${startDate}〜${endDate}にメンテナンスモードに切り替わります。期間中はサービスをご利用いただけません。`;
  }, [maintenance]);

  const showMaintenancePopup = useMemo(() => {
    if (!maintenance) return false;

    if (closedPopup) return false;

    const maintenanceClosedTime: string | null =
      LocalStorageManager.Instance.getObject(
        AppConfig.Instance.LocalStorageKey["maintenanceClosedTime"],
      ) ?? null;

    const oneHourBeforeStartDate = getDateTimeByHourOffset(
      maintenance.start_date,
      -1,
    );

    if (
      maintenanceClosedTime &&
      isInDateRange(maintenanceClosedTime, new Date(), oneHourBeforeStartDate)
    ) {
      return false;
    }

    return (
      maintenance.is_maintenance &&
      isNowInDateRange(oneHourBeforeStartDate, maintenance.start_date)
    );
  }, [maintenance, closedPopup]);

  const handleCloseMaintenancePopup = useCallback(() => {
    LocalStorageManager.Instance.saveObject(
      AppConfig.Instance.LocalStorageKey["maintenanceClosedTime"],
      new Date(),
    );

    setClosedPopup(true);
  }, []);

  return {
    popupTitle,
    showMaintenancePopup,
    handleCloseMaintenancePopup,
  };
};
